import React, { useEffect, useState } from "react";

import axios from "axios";
import {
  Box,
  Grid,
  Alert,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FormTextField from "../FormTextField";
import FileUploadField from "../FileUploadField";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useUpdateDriverLead } from "../../../hooks/use-driver";
import { useCalculateEmi } from "../../../hooks/use-emicalculation";
import { uploadCrifReport } from "../utils/uploadCibilDocuments";
import { formatAmount } from "../../utils/formatAmount";
import { endpoints } from "../../../api/endpoint";

const DRIVER_ID_CHECK_DEBOUNCE_MS = 500;

const safeAmount = (value) => formatAmount(Number(value) || 0);

const safeInterestRate = (rate) =>
  typeof rate === "number" && !Number.isNaN(rate)
    ? `${(rate * 100).toFixed(0)}%`
    : "--";

const safeEmiValue = (emi, key) => {
  const value = emi?.[key];
  return value != null && !Number.isNaN(Number(value))
    ? safeAmount(value)
    : "--";
};

const getFinancierKey = (option, index) =>
  option?.financierInternal || option?.financier || `financier-${index}`;

const EMI_CADENCES = [
  { key: "monthly", label: "Monthly" },
  { key: "weekly", label: "Weekly" },
  { key: "daily", label: "Daily" },
];

const CrifCibilEntryStep = ({ data: lead, refetchTableData }) => {
  const [cibilNumber, setCibilNumber] = useState("");
  const [crifReportUrl, setCrifReportUrl] = useState(null);
  const [driverId, setDriverId] = useState("");
  const [driverIdCheck, setDriverIdCheck] = useState({
    status: "idle",
    message: "",
  });
  const [checklistChecks, setChecklistChecks] = useState({
    crif: false,
    emi: false,
    driverId: false,
  });
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [results, setResults] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  const [actionError, setActionError] = useState(null);

  const {
    mutate: calculateEmi,
    isPending: isCalculating,
    isError: isCalcError,
    error: calcError,
  } = useCalculateEmi("v2");
  const { mutate: updateDriverLead, isPending: isSaving } =
    useUpdateDriverLead();

  useEffect(() => {
    if (!lead) return;
    setCibilNumber(lead.cibilScore || "");
    setCrifReportUrl(lead.documentUpload?.crifReportUrl || null);
    setDriverId(lead.driverId || "");
    setResults(lead.eligibleFinanciers || []);
    setChecklistChecks({ crif: false, emi: false, driverId: false });
    setSavedSuccessfully(false);
  }, [lead]);

  useEffect(() => {
    const trimmed = String(driverId || "").trim();

    if (!trimmed) {
      setDriverIdCheck({ status: "idle", message: "" });
      return undefined;
    }

    if (trimmed === String(lead?.driverId || "").trim()) {
      setDriverIdCheck({ status: "unique", message: "" });
      return undefined;
    }

    setDriverIdCheck({ status: "checking", message: "" });

    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(endpoints.checkDriverFieldUniqueness, {
          params: { driverId: trimmed },
        });

        if (data?.userPresent) {
          setDriverIdCheck({
            status: "taken",
            message: "This Driver ID is already in use",
          });
        } else {
          setDriverIdCheck({ status: "unique", message: "" });
        }
      } catch (err) {
        setDriverIdCheck({
          status: "error",
          message: getErrorMessage(err, "Failed to verify Driver ID"),
        });
      }
    }, DRIVER_ID_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [driverId, lead?.driverId]);

  const leadId = lead?.leadId || null;

  const invoiceAmount = Number(lead?.invoiceValue) || 0;
  const downPayment = Number(lead?.downpayment) || 0;
  const tenure = Number(lead?.emiTenure) || 0;

  const cibilError =
    showErrors && !String(cibilNumber || "").trim()
      ? "This field is required"
      : "";
  const crifError =
    showErrors && !crifReportUrl ? "This document is required" : "";
  const driverIdError =
    driverIdCheck.status === "taken" || driverIdCheck.status === "error"
      ? driverIdCheck.message
      : showSaveErrors && !String(driverId || "").trim()
        ? "This field is required"
        : "";

  const canCalculate =
    Boolean(cibilNumber) &&
    Boolean(crifReportUrl) &&
    invoiceAmount > 0 &&
    downPayment > 0 &&
    tenure > 0 &&
    (lead?.cibilScore !== Number(cibilNumber) || crifReportUrl instanceof File);

  const checklist = [
    {
      key: "crif",
      label: "CRIF report uploaded.",
      ready: Boolean(crifReportUrl),
    },
    {
      key: "emi",
      label: "Manually verified the EMI calculation.",
      ready: isCalculated && results.length > 0,
    },
    {
      key: "driverId",
      label: "Driver ID entered.",
      ready:
        Boolean(String(driverId || "").trim()) &&
        driverIdCheck.status === "unique",
    },
  ];

  const canSave =
    checklist.every((item) => checklistChecks[item.key]) && !savedSuccessfully;

  const markChanged = () => setSavedSuccessfully(false);

  const handleCibilChange = (value) => {
    setCibilNumber(value);
    markChanged();
  };

  const handleCrifChange = (value) => {
    setCrifReportUrl(value);
    markChanged();
  };

  const handleDriverIdChange = (value) => {
    setDriverId(value);
    markChanged();
  };

  const handleChecklistToggle = (key) => {
    setChecklistChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCalculate = async () => {
    setActionError(null);
    setShowErrors(true);
    markChanged();

    if (!cibilNumber || !crifReportUrl) return;

    const cibilScore = parseInt(cibilNumber, 10) || 0;
    if (!invoiceAmount || !tenure || !downPayment || !cibilScore) return;

    let uploadedCrifUrl = crifReportUrl;

    if (crifReportUrl instanceof File) {
      setIsUploading(true);
      try {
        const uploaded = await uploadCrifReport(crifReportUrl, leadId);
        uploadedCrifUrl = uploaded.crifReportUrl;
        setCrifReportUrl(uploadedCrifUrl);
      } catch (err) {
        setActionError(getErrorMessage(err, "Failed to upload CRIF report"));
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    calculateEmi(
      {
        cibilScore,
        downPayment,
        tenure,
        invoiceAmount,
      },
      {
        onSuccess: (response) => {
          setIsCalculated(true);
          if (!response || typeof response !== "object") {
            setResults([]);
            return;
          }

          const { mode, ...financierResults } = response;
          const parsedResults = Object.values(financierResults || {}).filter(
            (item) => item && typeof item === "object",
          );

          setResults(parsedResults);
        },
        onError: (err) => {
          setActionError(
            getErrorMessage(err, "Failed to calculate eligible financiers"),
          );
        },
      },
    );
  };

  const handleSave = () => {
    setActionError(null);
    setShowSaveErrors(true);

    if (!canSave) return;

    updateDriverLead(
      {
        leadId,
        payload: {
          documentUpload: {
            crifReportUrl,
          },
          cibilScore: Number(cibilNumber),
          eligibleFinanciers: results,
          driverId,
        },
      },
      {
        onSuccess: () => {
          setSavedSuccessfully(true);
          refetchTableData();
        },
        onError: (err) => {
          setActionError(
            getErrorMessage(err, "Failed to save eligible financiers"),
          );
        },
      },
    );
  };

  return (
    <Paper
      sx={{
        py: 3,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Typography
        sx={{ fontSize: 18, fontWeight: 700, mb: 3, color: "#12181A" }}
      >
        CIBIL & Financier Eligibility
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormTextField
            label="CIBIL Number"
            required
            type="number"
            value={cibilNumber}
            onChange={handleCibilChange}
            error={cibilError}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FileUploadField
            label="CRIF Report"
            required
            value={crifReportUrl}
            onChange={handleCrifChange}
            error={crifError}
          />
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="center" mt={1}>
          <Button
            variant="contained"
            onClick={handleCalculate}
            disabled={!canCalculate || isCalculating || isUploading}
            sx={{
              backgroundColor: "#0E7A6E",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 4,
              py: 1.1,
              fontSize: 14.5,
              boxShadow: "none",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: "#0B6359", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "#CFE3E0", color: "#fff" },
            }}
          >
            {isCalculating || isUploading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                Calculating...
              </Box>
            ) : (
              "Calculate EMI"
            )}
          </Button>
        </Grid>

        {actionError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {actionError}
            </Alert>
          </Grid>
        )}

        {savedSuccessfully && !actionError && (
          <Grid item xs={12}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Saved successfully. Change something to save again.
            </Alert>
          </Grid>
        )}

        {results.length === 0 &&
          cibilNumber &&
          !isCalculating &&
          isCalculated && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No financier is eligible for a CIBIL score of {cibilNumber} with
                the current downpayment and tenure.
              </Alert>
            </Grid>
          )}

        {isCalcError && (
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 13, color: "#B3261E" }}>
              {getErrorMessage(calcError, "Failed to calculate EMI")}
            </Typography>
          </Grid>
        )}

        {results?.length > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mt: 1,
              }}
            >
              {results.map((option, index) => {
                const financierKey = getFinancierKey(option, index);

                return (
                  <Card
                    key={financierKey}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #E8ECEB",
                      boxShadow: "0 1px 2px rgba(18,24,26,0.04)",
                      transition:
                        "box-shadow 0.15s ease, border-color 0.15s ease",
                      "&:hover": {
                        borderColor: "#CFE3E0",
                        boxShadow: "0 4px 16px rgba(14,122,110,0.08)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 15.5,
                          mb: 2,
                          color: "#12181A",
                        }}
                      >
                        {option?.financier || "Unknown financier"}
                      </Typography>

                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Row
                          label="Loan Amount"
                          value={
                            option?.loanAmount != null
                              ? safeAmount(option.loanAmount)
                              : "--"
                          }
                        />
                        <Row
                          label="Interest Rate"
                          value={safeInterestRate(option?.interestRate)}
                        />
                        <Row
                          label="Tenure"
                          value={
                            option?.tenure != null
                              ? `${option.tenure} months`
                              : "--"
                          }
                        />
                      </Stack>

                      <Box
                        sx={{
                          backgroundColor: "#F0F8F6",
                          borderRadius: "12px",
                          p: 1.75,
                          display: "flex",
                          width: "100%",
                        }}
                      >
                        {EMI_CADENCES?.map((cadence, cadenceIndex) => (
                          <Box
                            key={cadence.key}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              textAlign: "center",
                              borderLeft:
                                cadenceIndex === 0
                                  ? "none"
                                  : "1px solid rgba(14,122,110,0.15)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: 10.5, sm: 11 },
                                color: "#4C7A73",
                                fontWeight: 600,
                              }}
                            >
                              {cadence.label}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: {
                                  xs: cadence.key === "monthly" ? 17 : 13.5,
                                  sm: cadence.key === "monthly" ? 18 : 14.5,
                                },
                                fontWeight: 700,
                                color: "#0B5F56",
                                mt: 0.5,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {safeEmiValue(option?.emi, cadence.key)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Grid>
        )}

        {isCalculated && (
          <>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #E8ECEB",
                  borderRadius: "12px",
                  p: 2,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    mb: 1.5,
                    color: "#12181A",
                  }}
                >
                  Checklist
                </Typography>
                <Stack spacing={0.5}>
                  {checklist.map((item) => (
                    <FormControlLabel
                      key={item.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={checklistChecks[item.key]}
                          disabled={!item.ready}
                          onChange={() => handleChecklistToggle(item.key)}
                          sx={{
                            color: "#A6B4B0",
                            "&.Mui-checked": { color: "#0E7A6E" },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: item.ready ? "#12181A" : "#A6B4B0",
                          }}
                        >
                          {item.label}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormTextField
                label="Driver ID"
                required
                value={driverId}
                onChange={handleDriverIdChange}
                error={driverIdError}
              />
              {driverIdCheck.status === "checking" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.5,
                  }}
                >
                  <CircularProgress size={12} sx={{ color: "#66766F" }} />
                  <Typography sx={{ fontSize: 12, color: "#66766F" }}>
                    Checking availability...
                  </Typography>
                </Box>
              )}
              {driverIdCheck.status === "unique" &&
                String(driverId || "").trim() && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mt: 0.5,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 14, color: "#0E7A6E" }} />
                    <Typography sx={{ fontSize: 12, color: "#0E7A6E" }}>
                      Driver ID is available
                    </Typography>
                  </Box>
                )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="center" mt={1}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  !canSave || isSaving || driverIdCheck.status === "checking"
                }
                sx={{
                  backgroundColor: "#0E7A6E",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 4,
                  py: 1.1,
                  fontSize: 14.5,
                  boxShadow: "none",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: "#0B6359", boxShadow: "none" },
                  "&.Mui-disabled": {
                    backgroundColor: "#CFE3E0",
                    color: "#fff",
                  },
                }}
              >
                {isSaving ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                    Saving...
                  </Box>
                ) : (
                  "Save"
                )}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
    <Typography sx={{ fontSize: 13, color: "#66766F" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#12181A" }}>
      {value}
    </Typography>
  </Box>
);

export default CrifCibilEntryStep;
